import React, { useEffect, useRef } from 'react';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

// actions
import { showMutualConnectionsModal } from 'actions/mutualConnectionsActions';

// components
import LevelMarker from 'components/level/LevelMarker';
import { Spinner, Card } from 'ui-connect';

// utils
import NavigationUtil from 'utils/NavigationUtil';
import { pushNewsFeedAnalytics } from 'utils/newsFeed/newsFeedUtil';
import Translate from 'components/Translate';
import { isStatusNoConnection } from 'utils/connectionStatus/connectionStatusUtils';
import useMobileView from 'utils/hooks/useMobileView';
import { GROUP_ROLE } from 'utils/groups/GroupsUtil';
// utils -- userSocialProfileImage
import profileImageHelperV2 from 'utils/userSocialProfileImage/profileImageHelperV2';
import 'utils/userSocialProfileImage/profileImageTypeDefsV2';

// redux
import { selectProfileImageDtoMapState } from 'reducers/userSocialProfileImage/userSocialProfileImageReducer';

// styles
import styles from './ConnectionList.module.css';
import skeletonLoadingStyles from 'components/SkeletonLoadingCards/SkeletonLoadingCards.module.css';

const mapDispatchToProps = {
    showMutualConnectionsModal
};

const ConnectionList = ({
    // props
    viewerDisplayName,
    connectionList,
    shouldAnimate,
    resetShouldAnimate,
    actionIndex,
    closeModal,
    forceCloseOnNavigation,
    wrapItemInCard,
    showSpinner,
    hasMore,
    fetchNext,
    showFriendOnGarmin,
    // props from mapDispatchToProps
    showMutualConnectionsModal,
    disableMutualConnection
}) => {
    const loadingRef = useRef(null);

    const isMobileView = useMobileView();

    /**
     * @type {{[profileId:string]: SimplePublicSocialProfileV2DTO}
     */
    const profileImageDtoMapState = useSelector(selectProfileImageDtoMapState);

    useEffect(() => {
        if (hasMore && loadingRef.current) {
            const option = {
                root: null,
                rootMargin: '0px',
                threshold: 0.5
            };
            const observer = new IntersectionObserver(handleIntersect, option);
            observer.observe(loadingRef.current);

            return () => {
                observer.disconnect();
            };
        }
    }, [hasMore]);

    const handleIntersect = (entries) => {
        if (entries[0].isIntersecting) {
            fetchNext();
        }
    };

    const getConnectionDescription = ({
        showMutualConnection,
        mutualConnectionsCount,
        hasLocation,
        location,
        connectionStatus
    }) => {
        // Priority: Mutual Connection > Location > "Friend on Garmin" (so far it's used only by Suggested Connections)
        if (showMutualConnection) {
            return mutualConnectionsCount > 1 ? (
                <Translate
                    ns="connections"
                    content="multiple_mutual_connections"
                    params={{ 0: mutualConnectionsCount }}
                />
            ) : (
                <Translate ns="connections" content="one_mutual_connection" params={{ 0: 1 }} />
            );
        }

        if (hasLocation) {
            return location;
        }

        if (showFriendOnGarmin && isStatusNoConnection(connectionStatus)) {
            return <Translate ns="connections" content="friend_on_garmin" />;
        }

        return null;
    };

    const getNameSection = ({
        nameToDisplay,
        displayName,
        mutualConnectionsCount,
        location,
        nameInFullWidth,
        connectionStatus,
        groupRole,
        action
    }) => {
        const viewerIsOwner = viewerDisplayName === displayName;
        const hasMutualConnection = typeof mutualConnectionsCount === 'number' && mutualConnectionsCount > 0;
        // We don't show mutualConnection info for connection list on MutualConnectionsModal to prevent endless/nested modal popup
        const showMutualConnection = !disableMutualConnection && !viewerIsOwner && hasMutualConnection;
        const hasLocation = typeof location === 'string' && location.length !== 0;
        const isGroupOwner = groupRole === GROUP_ROLE.OWNER;
        const isGroupAdmin = groupRole === GROUP_ROLE.ADMIN;
        const handleClick = showMutualConnection
            ? (e) => handleMutualConnectionTextClicked(e, displayName, mutualConnectionsCount)
            : () => {};
        return (
            <div className={styles.userInfoContainer}>
                <div className={`${styles.nameWrapper} ${nameInFullWidth || isMobileView ? styles.fullWidth : ''}`}>
                    {isMobileView && action ? (
                        <a
                            className={styles.userName}
                            href={NavigationUtil.url(`/profile/${displayName}`)}
                            onClick={(e) => {
                                pushNewsFeedAnalytics('Post Details', 'User Profile');
                                NavigationUtil.anchorTagHandler(e);
                                if (forceCloseOnNavigation) closeModal();
                            }}
                        >
                            {nameToDisplay}
                        </a>
                    ) : (
                        <div className={styles.userName}>{nameToDisplay}</div>
                    )}
                    {isGroupOwner && (
                        <div className={styles.groupRoleLabel}>
                            <Translate ns="group" content={'owner'} />
                        </div>
                    )}
                    {isGroupAdmin && (
                        <div className={styles.groupRoleLabel}>
                            <Translate ns="group" content={'admin'} />
                        </div>
                    )}
                </div>
                <div
                    className={`${styles.textConnectionDescription} ${showMutualConnection ? styles.clickable : ''} ${
                        nameInFullWidth ? styles.fullWidth : ''
                    }`}
                    onClick={handleClick}
                >
                    {getConnectionDescription({
                        showMutualConnection,
                        mutualConnectionsCount,
                        hasLocation,
                        location,
                        connectionStatus
                    })}
                </div>
                {isMobileView && action && <div className={styles.actionWrapper}>{action}</div>}
            </div>
        );
    };

    const handleMutualConnectionTextClicked = (e, displayName, mutualConnectionsCount) => {
        e.preventDefault();
        e.stopPropagation(); // the purpose here is to stop passing click event forward, which would lead to page navigation to profile page
        showMutualConnectionsModal({ displayName, show: true, count: mutualConnectionsCount });
    };

    const renderUserInfo = ({
        fullName,
        displayName,
        profileImage,
        profileImageUrlSmall,
        profileImageSmall,
        mutualConnectionsCount,
        action,
        isDismissing,
        userLevel,
        location,
        connectionStatus,
        disableNavigation,
        groupRole,
        userId,
        userProfilePk,
        userProfileId
    }) => {
        /**
         * note: `userId`, `userProfilePk`, `userProfileId` are all same field from user
         * but were in different in field from different api
         */
        const profileImageDtoOfUser = profileImageDtoMapState?.[userId || userProfilePk || userProfileId];

        const fallbackUrl = profileImage || profileImageUrlSmall || profileImageSmall;
        const profileImageDtoSrc = profileImageHelperV2.getDtoSmallIconImage(profileImageDtoOfUser, fallbackUrl);
        const noclip = profileImageHelperV2.isNoclipProfile(profileImageDtoSrc);

        const nameToDisplay = fullName || displayName;
        if (disableNavigation) {
            // so far, this is only for user that's a kid account, for example, a kid joining a vivofit jr. challenge
            // and likes a comment of the challenge leaderboard
            return (
                <div className={`${styles.userWrapper} ${isDismissing ? styles.hide : styles.show}`}>
                    <div className={styles.avatarWrapper}>
                        <img
                            className={styles.avatar}
                            src={profileImageDtoSrc}
                            role="presentation"
                            alt=""
                            data-noclip={noclip}
                            onError={(evt) => {
                                evt.currentTarget.src = profileImageHelperV2.getDefaultProfileImage();
                                delete evt.currentTarget.dataset.noclip;
                            }}
                        />
                        {typeof userLevel === 'number' && <LevelMarker level={userLevel} small={true} />}
                    </div>
                    {getNameSection({
                        nameToDisplay,
                        displayName,
                        mutualConnectionsCount,
                        location,
                        nameInFullWidth: !action,
                        connectionStatus,
                        groupRole,
                        action
                    })}
                </div>
            );
        }

        // if it's in Mobile View and it has action buttons below, then we don't make the whole item clickable, just the user name and avatar
        return isMobileView && action ? (
            <div className={`${styles.userWrapper} ${isDismissing ? styles.hide : styles.show}`}>
                <a
                    className={styles.avatarWrapper}
                    href={NavigationUtil.url(`/profile/${displayName}`)}
                    onClick={(e) => {
                        pushNewsFeedAnalytics('Post Details', 'User Profile');
                        NavigationUtil.anchorTagHandler(e);
                        if (forceCloseOnNavigation) closeModal();
                    }}
                >
                    <img
                        className={styles.avatar}
                        src={profileImageDtoSrc}
                        role="presentation"
                        alt=""
                        data-noclip={noclip}
                        onError={(evt) => {
                            evt.currentTarget.src = profileImageHelperV2.getDefaultProfileImage();
                            delete evt.currentTarget.dataset.noclip;
                        }}
                    />
                    {typeof userLevel === 'number' && <LevelMarker level={userLevel} small={true} />}
                </a>
                {getNameSection({
                    nameToDisplay,
                    displayName,
                    mutualConnectionsCount,
                    location,
                    nameInFullWidth: !action,
                    connectionStatus,
                    groupRole,
                    action
                })}
            </div>
        ) : (
            <a
                className={`${styles.userWrapper} ${isDismissing ? styles.hide : styles.show}`}
                href={NavigationUtil.url(`/profile/${displayName}`)}
                onClick={(e) => {
                    pushNewsFeedAnalytics('Post Details', 'User Profile');
                    NavigationUtil.anchorTagHandler(e);
                    if (forceCloseOnNavigation) closeModal();
                }}
            >
                <div className={styles.avatarWrapper}>
                    <img
                        className={styles.avatar}
                        src={profileImageDtoSrc}
                        role="presentation"
                        alt=""
                        data-noclip={noclip}
                        onError={(evt) => {
                            evt.currentTarget.src = profileImageHelperV2.getDefaultProfileImage();
                            delete evt.currentTarget.dataset.noclip;
                        }}
                    />
                    {typeof userLevel === 'number' && <LevelMarker level={userLevel} small={true} />}
                </div>
                {getNameSection({
                    nameToDisplay,
                    displayName,
                    mutualConnectionsCount,
                    location,
                    nameInFullWidth: !action,
                    connectionStatus,
                    groupRole,
                    action
                })}
            </a>
        );
    };

    const renderConnectionList = () => {
        return connectionList.map((connection, index) => {
            const { displayName, action, dismissingState, isDismissing } = connection;
            return (
                <div
                    className={`${styles.itemContainer} ${wrapItemInCard ? styles.card : ''} ${
                        isDismissing ? styles.dismissing : ''
                    }${shouldAnimate && actionIndex !== -1 && index >= actionIndex ? styles.animated : ''}`}
                    onAnimationEnd={() => resetShouldAnimate()}
                    key={`connection-${displayName}`}
                >
                    {renderUserInfo(connection)}
                    {!isMobileView && action && (
                        <div className={`${styles.actionWrapper} ${isDismissing ? styles.hide : styles.show}`}>
                            {action}
                        </div>
                    )}
                    <div className={`${styles.dismissingOverlay} ${isDismissing ? styles.show : styles.hide}`}>
                        {dismissingState}
                    </div>
                </div>
            );
        });
    };

    const renderLoadingComponent = () => {
        let loadingComponent = (
            <div ref={loadingRef} className={styles.loaderContainer}>
                <div className={`skeleton-object ${skeletonLoadingStyles.skeletonAvatar} ${styles.avatar}`} />
                <div className={`${skeletonLoadingStyles.skeletonBarWrapper} ${styles.barWrapper}`}>
                    <div className={`skeleton-object ${skeletonLoadingStyles.skeletonBarOne}`} />
                    <div className={`skeleton-object ${skeletonLoadingStyles.skeletonBarTwo}`} />
                </div>
            </div>
        );
        if (wrapItemInCard) loadingComponent = <Card>{loadingComponent}</Card>;
        return loadingComponent;
    };

    return (
        <>
            {showSpinner ? (
                <div className={styles.spinner}>
                    <Spinner />
                </div>
            ) : (
                renderConnectionList()
            )}
            {hasMore && renderLoadingComponent()}
        </>
    );
};

ConnectionList.defaultProps = {
    viewerDisplayName: '',
    shouldAnimate: false,
    resetShouldAnimate: () => {},
    actionIndex: -1,
    wrapItemInCard: false,
    closeModal: () => {},
    forceCloseOnNavigation: false,
    hasMore: false,
    fetchNext: () => {},
    showFriendOnGarmin: false,
    disableMutualConnection: false
};

ConnectionList.propTypes = {
    viewerDisplayName: PropTypes.string,
    connectionList: PropTypes.array.isRequired,
    showSpinner: PropTypes.bool.isRequired,
    shouldAnimate: PropTypes.bool,
    resetShouldAnimate: PropTypes.func,
    actionIndex: PropTypes.number,
    wrapItemInCard: PropTypes.bool,
    closeModal: PropTypes.func,
    forceCloseOnNavigation: PropTypes.bool,
    hasMore: PropTypes.bool,
    fetchNext: PropTypes.func,
    showFriendOnGarmin: PropTypes.bool,
    disableMutualConnection: PropTypes.bool
};

export default connect(null, mapDispatchToProps)(ConnectionList);
